import type Database from 'better-sqlite3';

interface EdgeRecord {
  source_node_id: number;
  target_node_id: number;
}

/**
 * Cached PageRank result keyed by the Database *instance* (not its name).
 *
 * In-memory SQLite databases all report `db.name === ':memory:'`, so keying on
 * the filename collides across distinct in-memory DBs (tests, and every project
 * in the multi-project daemon) — a `{dbName, edgeCount}` slot would serve one
 * DB's result for another DB with the same edge count. Keying on the Database
 * object itself makes the cache identity-scoped, and the entry is still
 * validated by the resolved-edge COUNT to catch graph mutations.
 */
let _cache: WeakMap<object, { edgeCount: number; result: Map<number, number> }> = new WeakMap();

/** Invalidate the cache (call after bulk edge inserts, e.g. reindex) */
export function invalidatePageRankCache(): void {
  // WeakMap has no clear(); reassign a fresh instance to drop all entries.
  _cache = new WeakMap();
}

/**
 * Simple PageRank on the edges graph.
 * Results are cached and invalidated when the edge count changes.
 */
export function computePageRank(
  db: Database.Database,
  iterations = 20,
  dampingFactor = 0.85,
): Map<number, number> {
  // Fast cache check: if same DB and edge count hasn't changed, reuse.
  //
  // Uses an UNFILTERED `COUNT(*) FROM edges` rather than `WHERE resolved = 1`.
  // SQLite has no maintained row-count metadata for a filtered predicate —
  // even a covering index requires scanning every matching entry to tally
  // the count, which measured ~0.36ms on this project's own index (30k
  // edges) vs ~0.004ms for the unfiltered form (a hot per-search-query cost,
  // since this cache check runs on every `search()` call, fusion or not).
  // The unfiltered count is a correctness-preserving proxy here because the
  // only two places that mutate `resolved` state (`IndexingPipeline.runPipeline`
  // and `fireEdgeReconcile` in src/indexer/pipeline.ts) already call
  // `invalidatePageRankCache()` explicitly after every run — this DB-side
  // check is a redundant defense-in-depth guard, not the source of truth for
  // staleness. Edge insert/delete (the dominant real-world case) still
  // changes the unfiltered total and is still caught.
  const countRow = db.prepare('SELECT COUNT(*) as cnt FROM edges').get() as {
    cnt: number;
  };
  const cached = _cache.get(db);
  if (cached && cached.edgeCount === countRow.cnt) {
    return cached.result;
  }

  const edges = db
    .prepare('SELECT source_node_id, target_node_id FROM edges WHERE resolved = 1')
    .all() as EdgeRecord[];

  // Collect all unique node IDs
  const nodeSet = new Set<number>();
  const outgoing = new Map<number, number[]>();

  for (const edge of edges) {
    nodeSet.add(edge.source_node_id);
    nodeSet.add(edge.target_node_id);

    let targets = outgoing.get(edge.source_node_id);
    if (!targets) {
      targets = [];
      outgoing.set(edge.source_node_id, targets);
    }
    targets.push(edge.target_node_id);
  }

  const nodes = Array.from(nodeSet);
  const n = nodes.length;
  if (n === 0) return new Map();

  // Initialize scores
  const scores = new Map<number, number>();
  const initialScore = 1 / n;
  for (const nodeId of nodes) {
    scores.set(nodeId, initialScore);
  }

  // Iterate
  const base = (1 - dampingFactor) / n;

  for (let iter = 0; iter < iterations; iter++) {
    const newScores = new Map<number, number>();
    for (const nodeId of nodes) {
      newScores.set(nodeId, base);
    }

    // Accumulate rank mass lost to sink nodes (no outgoing edges) and redistribute
    // evenly across all nodes to conserve total rank mass.
    let sinkMass = 0;

    for (const nodeId of nodes) {
      const targets = outgoing.get(nodeId);
      if (!targets || targets.length === 0) {
        sinkMass += (scores.get(nodeId) ?? 0) * dampingFactor;
        continue;
      }

      const share = ((scores.get(nodeId) ?? 0) * dampingFactor) / targets.length;
      for (const target of targets) {
        newScores.set(target, (newScores.get(target) ?? base) + share);
      }
    }

    // Distribute sink mass equally to all nodes
    if (sinkMass > 0) {
      const sinkShare = sinkMass / n;
      for (const nodeId of nodes) {
        newScores.set(nodeId, (newScores.get(nodeId) ?? base) + sinkShare);
      }
    }

    for (const nodeId of nodes) {
      scores.set(nodeId, newScores.get(nodeId) ?? base);
    }
  }

  _cache.set(db, { edgeCount: countRow.cnt, result: scores });
  return scores;
}
